import type {
  AgentStoryPayload,
  MintedInvite,
  PrimitivesInfo,
  ReplayPayload,
  RunStatus,
  SimulationDef,
  TeamInvite,
  TeamMember,
} from "./types";

/** Hosted-mode seam: live mode (the default) is same-origin with no auth;
 *  the hosted site points the client at the snapshot API and attaches the
 *  Guardius bearer token. Configured once at boot (Root.tsx). */
let apiBaseUrl = "";
let tokenGetter: (() => string | null) | null = null;
/** Per-user (definitions/runs/validate) routes live under `/api/studio` in
 *  member mode; showcase reads (scenarios/primitives) always stay on `/api`. */
let studioPrefix = "/api";

export function configureApi(options: {
  baseUrl: string;
  getToken: (() => string | null) | null;
  studio?: boolean;
}): void {
  apiBaseUrl = options.baseUrl;
  tokenGetter = options.getToken;
  studioPrefix = options.studio ? "/api/studio" : "/api";
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = tokenGetter?.() ?? null;
  if (token !== null) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(apiBaseUrl + path, {
    headers,
    ...init,
  });
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (body.detail) {
        detail = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail);
      }
    } catch {
      /* keep the status text */
    }
    throw new Error(detail);
  }
  return response.json() as Promise<T>;
}

export const api = {
  scenarios: () => request<{ name: string }[]>("/api/scenarios"),
  scenario: (name: string) =>
    request<Record<string, unknown>>(`/api/scenarios/${encodeURIComponent(name)}`),
  definitions: () => request<{ name: string }[]>(`${studioPrefix}/definitions`),
  definition: (name: string) =>
    request<Record<string, unknown>>(`${studioPrefix}/definitions/${encodeURIComponent(name)}`),
  saveDefinition: (name: string, definition: SimulationDef) =>
    request<{ name: string }>(`${studioPrefix}/definitions/${encodeURIComponent(name)}`, {
      method: "PUT",
      body: JSON.stringify(definition),
    }),
  deleteDefinition: (name: string) =>
    request<{ deleted: string }>(`${studioPrefix}/definitions/${encodeURIComponent(name)}`, {
      method: "DELETE",
    }),
  validate: (definition: SimulationDef) =>
    request<{ errors: string[] }>(`${studioPrefix}/validate`, {
      method: "POST",
      body: JSON.stringify(definition),
    }),
  primitives: () => request<PrimitivesInfo>("/api/primitives"),
  startRun: (definition: SimulationDef, ticks: number, name?: string) =>
    request<{ id: string }>(`${studioPrefix}/runs`, {
      method: "POST",
      body: JSON.stringify({ definition, ticks, name }),
    }),
  runStatus: (id: string) => request<RunStatus>(`${studioPrefix}/runs/${encodeURIComponent(id)}`),
  cancelRun: (id: string) =>
    request<{ cancelled: string }>(`${studioPrefix}/runs/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  replay: (id: string) =>
    request<ReplayPayload>(`${studioPrefix}/runs/${encodeURIComponent(id)}/replay`),
  runDefinition: (id: string) =>
    request<Record<string, unknown>>(`${studioPrefix}/runs/${encodeURIComponent(id)}/definition`),
  history: () => request<RunStatus[]>(`${studioPrefix}/runs`),
  agentStory: (runId: string, agent: number) =>
    request<AgentStoryPayload>(
      `${studioPrefix}/runs/${encodeURIComponent(runId)}/agents/${agent}/story`,
    ),
};

/** The owner invite console talks to the `/api/team/*` routes — a separate
 *  Lambda from the studio prefix, so these are always absolute (no studioPrefix). */
export const team = {
  members: () => request<{ members: TeamMember[] }>("/api/team/members"),
  invites: () => request<{ invites?: TeamInvite[] }>("/api/team/invites"),
  mintInvite: (body: { role: string; maxUses: number; expiresAt: string; label?: string }) =>
    request<MintedInvite>("/api/team/invites", { method: "POST", body: JSON.stringify(body) }),
  revokeInvite: (inviteId: string) =>
    request<unknown>(`/api/team/invites/${encodeURIComponent(inviteId)}/revoke`, {
      method: "POST",
    }),
};
